import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { ENV } from '../config/env';
import { AlertCircle, CheckCircle, Clock, Gift, X } from 'lucide-react';

const SubscriptionBanner = ({ onActivate }) => {
  const { user } = useAuth();
  const { getUserFeatureAccess, redeemCoupon, users } = useData();
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isRedeeming, setIsRedeeming] = useState(false);

  const featureAccess = getUserFeatureAccess(user?.id, 'offline_jobs');
  
  // Get user-specific subscription price (default to 29 if not set)
  const currentUser = users.find(u => u.id === user?.id);
  const subscriptionPrice = currentUser?.subscriptionPrice || ENV.SUBSCRIPTION.OFFLINE_JOBS_MONTHLY;
  
  // Check if access is active and not expired
  const isActive = featureAccess && featureAccess.isActive && 
    (featureAccess.expiresAt === null || new Date(featureAccess.expiresAt) > new Date());

  const daysRemaining = featureAccess && featureAccess.expiresAt ? 
    Math.ceil((new Date(featureAccess.expiresAt) - new Date()) / (1000 * 60 * 60 * 24)) : null;

  const handleRedeemCoupon = async () => {
    if (!couponCode.trim()) {
      setMessage({ type: 'error', text: 'Please enter a coupon code' });
      return;
    }

    setIsRedeeming(true);
    setMessage({ type: '', text: '' });

    try {
      const result = redeemCoupon(user.id, couponCode);
      
      if (result.success) {
        setMessage({ type: 'success', text: result.message });
        setCouponCode('');
        setTimeout(() => {
          setShowCouponModal(false);
          setMessage({ type: '', text: '' });
          if (onActivate) onActivate();
        }, 2000);
      } else {
        setMessage({ type: 'error', text: result.message });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to redeem coupon. Please try again.' });
    } finally {
      setIsRedeeming(false);
    }
  };

  // No active access
  if (!isActive) {
    return (
      <>
        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-4 mb-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 flex-1">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Gift className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Track Your Offline Jobs
                </h3>
                <p className="text-sm text-gray-700 mb-3">
                  Centralize all your pet care transactions in one place. Track external jobs, manage receipts, and analyze your spending/earnings.
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowCouponModal(true)}
                    className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 font-medium text-sm transition-colors"
                  >
                    Activate with Coupon
                  </button>
                  <span className="text-sm text-gray-600">₹{subscriptionPrice}/month</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Coupon Modal */}
        {showCouponModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">Enter Coupon Code</h3>
                <button
                  onClick={() => {
                    setShowCouponModal(false);
                    setMessage({ type: '', text: '' });
                    setCouponCode('');
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Coupon Code
                  </label>
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="Enter your coupon code"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase"
                    disabled={isRedeeming}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Try: TRIAL7, OFFLINE30, or TRACKFOREVER
                  </p>
                </div>

                {message.text && (
                  <div className={`p-3 rounded-lg ${
                    message.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                  }`}>
                    <div className="flex items-center gap-2">
                      {message.type === 'success' ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-600" />
                      )}
                      <p className={`text-sm ${message.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
                        {message.text}
                      </p>
                    </div>
                  </div>
                )}

                <button
                  onClick={handleRedeemCoupon}
                  disabled={isRedeeming || !couponCode.trim()}
                  className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isRedeeming ? 'Redeeming...' : 'Redeem Coupon'}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Active access - show status
  if (featureAccess.expiresAt === null) {
    // Lifetime access
    return (
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-green-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">Offline Jobs Tracking Active</h3>
            <p className="text-sm text-gray-700">Lifetime access • Activated with {featureAccess.couponCode}</p>
          </div>
        </div>
      </div>
    );
  }

  // Limited time access
  const isExpiringSoon = daysRemaining && daysRemaining <= 7;

  return (
    <div className={`bg-gradient-to-r ${
      isExpiringSoon ? 'from-yellow-50 to-orange-50 border-yellow-200' : 'from-green-50 to-emerald-50 border-green-200'
    } border rounded-xl p-4 mb-6`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1">
          <div className={`w-10 h-10 ${
            isExpiringSoon ? 'bg-yellow-100' : 'bg-green-100'
          } rounded-lg flex items-center justify-center flex-shrink-0`}>
            {isExpiringSoon ? (
              <Clock className="w-5 h-5 text-yellow-600" />
            ) : (
              <CheckCircle className="w-5 h-5 text-green-600" />
            )}
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">Offline Jobs Tracking Active</h3>
            <p className="text-sm text-gray-700">
              {daysRemaining > 0 ? (
                <>
                  {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining • Activated with {featureAccess.couponCode}
                </>
              ) : (
                <>Expires today • Activated with {featureAccess.couponCode}</>
              )}
            </p>
            {isExpiringSoon && (
              <button
                onClick={() => setShowCouponModal(true)}
                className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Renew with a new coupon →
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Coupon Modal (same as above) */}
      {showCouponModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Enter Coupon Code</h3>
              <button
                onClick={() => {
                  setShowCouponModal(false);
                  setMessage({ type: '', text: '' });
                  setCouponCode('');
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Coupon Code
                </label>
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  placeholder="Enter your coupon code"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase"
                  disabled={isRedeeming}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Try: TRIAL7, OFFLINE30, or TRACKFOREVER
                </p>
              </div>

              {message.text && (
                <div className={`p-3 rounded-lg ${
                  message.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                }`}>
                  <div className="flex items-center gap-2">
                    {message.type === 'success' ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    )}
                    <p className={`text-sm ${message.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
                      {message.text}
                    </p>
                  </div>
                </div>
              )}

              <button
                onClick={handleRedeemCoupon}
                disabled={isRedeeming || !couponCode.trim()}
                className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRedeeming ? 'Redeeming...' : 'Redeem Coupon'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionBanner;

